import React, { useEffect, useState } from "react";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { storage } from "../utils/firebase";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";

/* ── PreviewImage: image upload/delete with preview ── */

export const PreviewImage: React.FC<{
  value: string;
  project: string;
  contentType: string;
  onChange: (url: string) => void;
  onAutoSave?: (newUrl: string) => void;
}> = ({ value, project, contentType, onChange, onAutoSave }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = React.useRef<HTMLInputElement>(null);

  const hasImage = !!value && !value.startsWith("AUTO_") && value !== "AUTO_IMAGE_NAME" && value !== "AUTO_THUMB_NAME";

  useEffect(() => {
    if (!hasImage) { setPreviewUrl(null); return; }
    if (value.startsWith("http") || value.startsWith("data:")) { setPreviewUrl(value); return; }
    let cancelled = false;
    getDownloadURL(storageRef(storage, value))
      .then((u) => { if (!cancelled) setPreviewUrl(u); })
      .catch(() => { if (!cancelled) setPreviewUrl(null); });
    return () => { cancelled = true; };
  }, [value, hasImage]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const { uploadBytes } = await import("firebase/storage");
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      const base = file.name.slice(0, file.name.lastIndexOf("."))
        .toLowerCase().replace(/[^a-z0-9_-]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      const path = `content/${project}/${contentType}/images/${base}${ext}`;
      await uploadBytes(storageRef(storage, path), file);
      onChange(path);
      onAutoSave?.(path);
    } catch (err) {
      console.error("[PreviewImage] Upload error:", err);
    }
    setUploading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this image?")) return;
    try {
      const { deleteObject } = await import("firebase/storage");
      await deleteObject(storageRef(storage, value));
    } catch {}
    onChange("");
    onAutoSave?.("");
  };

  return (
    <div className="simple__image-field">
      <input ref={fileInput} type="file" accept="image/*" onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) handleUpload(f);
        e.target.value = "";
      }} style={{ display: "none" }} />
      {hasImage && previewUrl ? (
        <div className="simple__image-wrap">
          <img src={previewUrl} alt="" className="simple__image" />
          <div className="simple__image-actions">
            <button className="simple__image-btn" onClick={() => fileInput.current?.click()} title="Replace">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            <button className="simple__image-btn simple__image-btn--danger" onClick={handleDelete} title="Delete">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <button className="simple__image-upload" onClick={() => fileInput.current?.click()} disabled={uploading}>
          {uploading ? "Uploading..." : "Add image"}
        </button>
      )}
    </div>
  );
};

/* ── MetaThumb: thumbnail upload with crop modal ── */

async function getCroppedBlob(src: string, crop: Area): Promise<Blob> {
  const img = new Image();
  img.src = src;
  await new Promise((r) => { img.onload = r; });
  const canvas = document.createElement("canvas");
  canvas.width = 400;
  canvas.height = 400;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, 400, 400);
  return new Promise((r) => canvas.toBlob((b) => r(b!), "image/png"));
}

export const MetaThumb: React.FC<{
  value: string;
  project: string;
  contentType: string;
  onChange: (url: string) => void;
}> = ({ value, project, contentType, onChange }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const fileInput = React.useRef<HTMLInputElement>(null);

  const hasThumb = !!value && value !== "AUTO_THUMB_NAME";

  useEffect(() => {
    if (!hasThumb) { setPreviewUrl(null); return; }
    if (value.startsWith("http") || value.startsWith("data:")) { setPreviewUrl(value); return; }
    let cancelled = false;
    getDownloadURL(storageRef(storage, value))
      .then((u) => { if (!cancelled) setPreviewUrl(u); })
      .catch(() => { if (!cancelled) setPreviewUrl(null); });
    return () => { cancelled = true; };
  }, [value, hasThumb]);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!cropSrc || !croppedArea) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(cropSrc, croppedArea);
      const { uploadBytes } = await import("firebase/storage");
      const path = `content/${project}/${contentType}/thumbs/thumb_${Date.now()}.png`;
      await uploadBytes(storageRef(storage, path), blob);
      onChange(path);
      setCropSrc(null);
    } catch (err) {
      console.error("[MetaThumb] Upload error:", err);
    }
    setUploading(false);
  };

  return (
    <div className="editor__meta-thumb">
      <input ref={fileInput} type="file" accept="image/*" onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
        e.target.value = "";
      }} style={{ display: "none" }} />

      {cropSrc && (
        <div className="crop-modal" onClick={() => setCropSrc(null)}>
          <div className="crop-modal__body" onClick={(e) => e.stopPropagation()}>
            <div className="crop-modal__area">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, area) => setCroppedArea(area)}
              />
            </div>
            <div className="crop-modal__controls">
              <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="crop-modal__zoom" />
              <div className="crop-modal__actions">
                <button className="crop-modal__btn crop-modal__btn--cancel" onClick={() => setCropSrc(null)}>Cancel</button>
                <button className="crop-modal__btn crop-modal__btn--save" onClick={handleSave} disabled={uploading}>
                  {uploading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasThumb && previewUrl ? (
        <div className="editor__meta-thumb-preview" onClick={() => fileInput.current?.click()}>
          <img src={previewUrl} alt="" className="editor__meta-thumb-img" />
        </div>
      ) : (
        <button className="editor__meta-thumb-add" onClick={() => fileInput.current?.click()}>
          Thumb
        </button>
      )}
    </div>
  );
};
